/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

/**
 * useGridData.js
 * @description Custom React hook for managing grid data fetching, pagination, and filtering.
 * Encapsulates all grid-related state and logic including:
 * - Grid initialization and setup
 * - Data source configuration for infinite scrolling
 * - Filter and sort handling
 * - Page size management
 * - API data fetching via the Recoil-based fetchDataSelector
 *
 * @hook
 * @example
 * const { onGridReady, onRefreshClickHandler, onPageSizeChange } = useGridData(gridRef, '12345')
 */
import { useEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { fetchDataSelector } from '../../../../../components/recoil/selectors/selectors'
import utils from '../../../../../../utils/utils'
import {
  observationDetailsAtom,
  toastMessage,
} from '@components/recoil/atoms/atoms'
import { ManualReviewAPIEndpoints } from '@pages/manualReviewTab/api'

/**
 * Custom hook to manage grid data fetching and API calls
 * @param {Object} gridRef - Reference to the AG-Grid instance
 * @param {string} preCaseId - The ID of the pre-case to fetch data for
 * @returns {Object} Object containing grid handlers and configuration
 * @returns {Object} .gridApi - AG-Grid API instance
 * @returns {Function} .onGridReady - Handler called when grid is initialized
 * @returns {Function} .onRefreshClickHandler - Handler to refresh grid data and reset filters
 * @returns {Function} .onPageSizeChange - Handler to update pagination page size
 */
export const useGridData = (gridRef, preCaseId) => {
  const getCoreData = useRecoilValue(fetchDataSelector)
  const showToastMessage = useSetRecoilState(toastMessage)
  const [gridApi, setGridApi] = useState(null)
  const observationDetails = useRecoilValue(observationDetailsAtom)

  const onGridReady = (params) => {
    setGridApi(params.api)
  }

  /* istanbul ignore next */
  const onRefreshClickHandler = () => {
    if (!gridApi) return

    const sortModel = gridApi?.rowModel?.cacheParams?.sortModel
    const filterModel = gridApi?.rowModel?.cacheParams?.filterModel

    let refresh = false
    if (sortModel?.length > 0) {
      gridApi?.columnModel?.resetColumnState()
      refresh = true
    }
    if (Object.keys(filterModel || {}).length > 0) {
      gridApi?.setFilterModel(null)
      refresh = true
    }
    if (!refresh) {
      gridApi?.refreshInfiniteCache()
    }
  }

  const onPageSizeChange = (pageSize, onPageSizeCallback) => {
    if (!gridRef.current || !gridApi) return

    onPageSizeCallback(pageSize)
    gridRef.current.api.paginationGoToPage(0)
    gridApi.rowModel.cacheParams.blockSize = pageSize
    gridApi.paginationSetPageSize(pageSize)
    gridApi.purgeInfiniteCache()
  }

  /* istanbul ignore next */
  const getDataSource = () => ({
    getRows: (params) => {
      gridApi.showLoadingOverlay()
      const { startRow, endRow, filterModel, sortModel } = params

      const pageSize = endRow - startRow
      const pageNumber = endRow / pageSize

      const sortColumnName = sortModel?.length ? sortModel[0].colId : ''
      const sortOrder = sortModel?.length ? sortModel[0].sort : ''

      let filterColumnName = ''
      let filterValue = ''

      if (filterModel) {
        Object.keys(filterModel).forEach((key) => {
          filterColumnName = key
          if (filterModel[key]?.filterType === 'date') {
            const filterObj = filterModel[key]
            filterValue =
              filterObj?.dateFrom ||
              filterObj?.condition1?.dateFrom ||
              filterObj?.condition1?.dateTo ||
              filterObj?.dateTo ||
              ''
          } else {
            const filterObj = filterModel[key]
            filterValue =
              filterObj?.filter || filterObj?.condition1?.filter || ''
          }
        })
      }

      const postData = {
        caseId: preCaseId,
        pageNumber: parseInt(pageNumber) === 0 ? 1 : parseInt(pageNumber),
        pageSize,
        sortColumnName,
        sortOrder,
        filterColumnName: filterValue && filterColumnName,
        filterValue,
      }

      getCoreData(
        ManualReviewAPIEndpoints?.getFuzzyVrmData?.method,
        ManualReviewAPIEndpoints?.getFuzzyVrmData?.url
          .replace('__observationId__', observationDetails?.id)
          .replace('__fuzzyVrm__', observationDetails?.vrmNo),
        postData,
        null,
        null,
        true,
        utils?.apiDomainName?.privateLand?.name
      )
        .then((response) => {
          if (response?.status === utils?.apiResponseCode?.successCode) {
            params.successCallback(
              response?.data?.data || [],
              response?.data?.totalCount || 0
            )
            if (response?.data?.data?.length > 0) {
              gridApi.hideOverlay()
            } else {
              gridApi.showNoRowsOverlay()
            }
          } else {
            params.successCallback([], 0)
            gridApi.showNoRowsOverlay()
            showToastMessage({
              message: utils?.errorOnGettingData,
              status: 'error',
            })
          }
        })
        .catch(() => {
          params.successCallback([], 0)
          gridApi.showNoRowsOverlay()
          showToastMessage({
            message: err?.response?.data,
            status: 'error',
          })
        })
    },
  })

  useEffect(() => {
    if (!gridApi) return
    gridApi.setDatasource(getDataSource())
  }, [gridApi, getCoreData, preCaseId])

  return {
    gridApi,
    onGridReady,
    onRefreshClickHandler,
    onPageSizeChange,
  }
}
